import { takeLatest, call, put, select } from 'redux-saga/effects';

// Services
import { productApi } from 'services/apiDigitalTicket/product';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import CONST_PRODUCT from 'constants/product';

import * as ProductActions from './actions';
import { ProductTypes } from './types';

function* list({
  payload,
}: ReturnType<typeof ProductActions.listProductsRequest>): any {
  try {
    const { body, filtering } = payload;
    if (!filtering) {
      yield put(
        DialogsActions.setOpenLoading({ title: 'Carregando produtos' }),
      );
    }

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(productApi.get, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      if ((body as { type: string })?.type === CONST_PRODUCT.BOOKED_TICKET) {
        yield put(
          ProductActions.listTicketResavationSuccess({
            ticketResavationList: response.data,
            totalCount: parseInt(response.headers.totalcount, 10),
          }),
        );
      } else {
        yield put(
          ProductActions.listProductsSuccess({
            productsList: response.data,
            totalCount: parseInt(response.headers.totalcount, 10),
          }),
        );
      }

      if (!filtering) {
        yield put(DialogsActions.setClose());
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('product - listProducts - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
        status: error.response.status,
      }),
    );
    yield put(ProductActions.listProductsFailure());

    if (!payload.filtering) {
      yield put(DialogsActions.setClose());
    }
  }
}

function* filter({
  payload,
}: ReturnType<typeof ProductActions.filterProductsRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(productApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        ProductActions.filterProductsSuccess({
          productsList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('product - filterProducts - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
        status: error.response.status,
      }),
    );
    yield put(ProductActions.filterProductsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
}: ReturnType<typeof ProductActions.createProductRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando produto' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(productApi.create, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        ProductActions.createProductSuccess({ product: response.data }),
      );

      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Produto criado com sucesso',
        }),
      );

      if (
        !((payload as { type: string }).type === CONST_PRODUCT.BOOKED_TICKET)
      ) {
        yield put(ProductActions.listProductsRequest({}));
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('product - createProduct - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ProductActions.createProductFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof ProductActions.updateProductRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando produto' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(productApi.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        ProductActions.updateProductSuccess({ product: response.data }),
      );

      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Produto alterado com sucesso',
        }),
      );
      if (
        !((payload as { type: string }).type === CONST_PRODUCT.BOOKED_TICKET)
      ) {
        yield put(ProductActions.listProductsRequest({}));
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('product - updateProduct - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ProductActions.updateProductFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof ProductActions.deleteProductRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo produto' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(productApi.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Produto excluído com sucesso',
        }),
      );

      yield put(DialogsActions.setClose());
      yield put(ProductActions.deleteProductSuccess());
      yield put(ProductActions.listProductsRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('product - remove - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ProductActions.deleteProductFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const productSagas = [
  takeLatest(ProductTypes.LIST_PRODUCTS_REQUEST, list),
  takeLatest(ProductTypes.FILTER_PRODUCTS_REQUEST, filter),
  takeLatest(ProductTypes.CREATE_PRODUCT_REQUEST, create),
  takeLatest(ProductTypes.UPDATE_PRODUCT_REQUEST, update),
  takeLatest(ProductTypes.DELETE_PRODUCT_REQUEST, remove),
];
